/**
 * Security Stats Hook
 *
 * Derives SecurityStats from audit event data in live/public mode.
 * Falls back to demo generators in demo mode.
 *
 * Maps audit eventsByType keys (Go audit service event_type column)
 * to SecurityStats fields:
 *   - "pii.detected"          → piiDetected / piiMasked
 *   - "injection.blocked"     → injectionsBlocked
 *   - "guardrails.processed"  → totalScanned
 *   - passRate computed from totals
 *
 * NOTE: eventsByType is grouped by event_type (e.g., "pii.detected"),
 * NOT Kafka topic names (e.g., "security.pii-detected").
 * See apps/audit-service/README.md for the mapping table.
 */

import { useMemo } from "react";
import { useAuditStats } from "./useAuditStats";
import { useDataMode } from "../providers/DataModeProvider";
import { generateSecurityStats } from "../demo/generators";
import type { SecurityStats } from "../types/metrics";

export function useSecurityStats(): {
  data: SecurityStats;
  isLoading: boolean;
} {
  const { mode } = useDataMode();
  const { data: auditStats, isLoading } = useAuditStats();

  const data = useMemo<SecurityStats>(() => {
    // Demo mode — use generator (existing behavior)
    if (mode === "demo") {
      return generateSecurityStats();
    }

    // Live/public — derive from real audit stats
    if (!auditStats) {
      // Still loading — return zeroes rather than demo data
      return {
        totalScanned: 0,
        piiDetected: 0,
        piiMasked: 0,
        injectionsBlocked: 0,
        passRate: 100,
      };
    }

    const byType = auditStats.eventsByType;
    // event_type values from the Go audit service (NOT Kafka topic names)
    const piiDetected = byType["pii.detected"] ?? 0;
    const injectionsBlocked = byType["injection.blocked"] ?? 0;
    const totalScanned =
      byType["guardrails.processed"] ?? auditStats.totalEvents;

    // All detected PII is masked by design (Presidio masks in-place)
    const piiMasked = piiDetected;

    // Pass rate: notes that passed guardrails without being blocked.
    // Clamped to [0, 100] in case injectionsBlocked counts events from
    // a broader scope than guardrails.processed.
    const passRate =
      totalScanned > 0
        ? Math.max(
            0,
            Math.min(
              100,
              Math.round(
                ((totalScanned - injectionsBlocked) / totalScanned) * 100,
              ),
            ),
          )
        : 100;

    return {
      totalScanned,
      piiDetected,
      piiMasked,
      injectionsBlocked,
      passRate,
    };
  }, [mode, auditStats]);

  return { data, isLoading };
}
